/* eslint-disable eag/match-named-export */
import {CategoryId} from '@mira/api';
import {MiraIconKey} from '@mira/ui';

import {MainCategoryGroupId, RowType, SubcategoryId} from '../schemas';

// ============================================================================
// MAIN GROUPS - 6 main car part groups (order defines display order)
// ============================================================================

export type MainGroupDefinition = {
  id: MainCategoryGroupId;
  title: string;
  icon: MiraIconKey;
  description: string;
};

export const MAIN_GROUPS: MainGroupDefinition[] = [
  {
    id: 'engine-parts',
    title: 'Engine',
    icon: 'mira/engine',
    description:
      'The engine is the core power unit of the vehicle and converts fuel into mechanical energy to drive the wheels. Faults can include issues with internal mechanical components, cooling, lubrication, air-fuel mixture, or engine electrical systems.',
  },
  {
    id: 'transmission',
    title: 'Transmission',
    icon: 'mira/transmission',
    description:
      'The transmission transfers engine power to the wheels and enables smooth changes in speed and torque. Faults can include worn clutches, slipping gears, damaged differentials, or vibrations from the drivetrain.',
  },
  {
    id: 'axles',
    title: 'Axles',
    icon: 'mira/axles',
    description:
      'The axles group covers steering, suspension, brakes, and wheels that keep the vehicle stable and controllable. Faults can range from uneven tire wear and misalignment to worn suspension joints, leaking shock absorbers, or reduced braking performance.',
  },
  {
    id: 'heating-air-condition',
    title: 'Heating & Air Condition',
    icon: 'mira/heating-air-condition',
    description:
      'The heating and air conditioning system regulates cabin temperature, humidity, and window demisting for comfort and visibility. Faults can include weak airflow, insufficient cooling or heating, unusual odors, or noisy blower and compressor operation.',
  },
  {
    id: 'body-chassis',
    title: 'Body / Chassis',
    icon: 'mira/body-chassis',
    description:
      "The chassis provides the vehicle's structural foundation and is fundamental to occupant safety during a collision. Faults can range from improper panel alignment to compromised structural points or malfunctioning door mechanisms.",
  },
  {
    id: 'electric-parts',
    title: 'Electric Parts',
    icon: 'mira/electric-parts',
    description:
      'The electrical system manages everything from the engine to safety systems, lighting, and cabin features. A single electrical fault can have cascading effects, potentially disabling critical functions.',
  },
];

// ============================================================================
// SUBCATEGORIES - 22 subcategories (order defines display order)
// ============================================================================

export type SubcategoryDefinition = {
  id: SubcategoryId;
  title: string;
  mainGroupId: MainCategoryGroupId;
};

export const SUBCATEGORIES: SubcategoryDefinition[] = [
  // Engine Parts (6)
  {id: 'engine-mechanical', title: 'Engine Mechanical & Timing', mainGroupId: 'engine-parts'},
  {id: 'engine-cooling', title: 'Engine Cooling', mainGroupId: 'engine-parts'},
  {id: 'engine-lubrication', title: 'Engine Lubrication', mainGroupId: 'engine-parts'},
  {id: 'engine-air-fuel', title: 'Air Intake, Fuel & Exhaust', mainGroupId: 'engine-parts'},
  {id: 'engine-electrical', title: 'Engine Electrical & Ignition', mainGroupId: 'engine-parts'},
  {id: 'engine-accessories', title: 'Engine Accessories', mainGroupId: 'engine-parts'},
  // Transmission (3)
  {id: 'gearbox', title: 'Gearbox', mainGroupId: 'transmission'},
  {id: 'clutch', title: 'Clutch & Linkage', mainGroupId: 'transmission'},
  {id: 'differential', title: 'Differential', mainGroupId: 'transmission'},
  // Chassis & Running Gear (4)
  {id: 'steering', title: 'Steering', mainGroupId: 'axles'},
  {id: 'suspension', title: 'Suspension', mainGroupId: 'axles'},
  {id: 'brake-system', title: 'Brake System', mainGroupId: 'axles'},
  {id: 'wheels-tires', title: 'Wheels & Tires', mainGroupId: 'axles'},
  // Climate Control (2)
  {id: 'heating', title: 'Heating', mainGroupId: 'heating-air-condition'},
  {id: 'air-conditioning', title: 'Air Conditioning', mainGroupId: 'heating-air-condition'},
  // Body & Interior (3)
  {id: 'bodywork', title: 'Bodywork', mainGroupId: 'body-chassis'},
  {id: 'doors-windows', title: 'Doors, Windows & Wipers', mainGroupId: 'body-chassis'},
  {id: 'comfort-features', title: 'Comfort Features', mainGroupId: 'body-chassis'},
  // Electrical & Electronics (4)
  {id: 'electrical-wiring', title: 'Electrical Wiring', mainGroupId: 'electric-parts'},
  {id: 'lighting', title: 'Lighting', mainGroupId: 'electric-parts'},
  {
    // Subcategory id kept as `instruments-infotainment` to avoid breaking the
    // shared SubcategoryId enum, but the title reflects what is actually
    // predicted: backend v2 excludes the `INFOTAINMENT` model and only
    // surfaces `INSTRUMENTS`, whose training data is dominated by diagnostic
    // tooling activity (see ml_features/categories.py: `přístroje`).
    id: 'instruments-infotainment',
    title: 'Instruments & Diagnostics',
    mainGroupId: 'electric-parts',
  },
  {id: 'safety-systems', title: 'Safety & Assistance Systems', mainGroupId: 'electric-parts'},
];

// ============================================================================
// CATEGORIES - 29 API categories mapped to subcategories
// ============================================================================

export type CategoryDefinition = {
  id: CategoryId;
  name: string;
  subcategoryId: SubcategoryId;
  operation: string;
};

export const CATEGORIES: CategoryDefinition[] = [
  {
    id: 'AIR_CONDITIONING',
    name: 'Air Conditioning',
    subcategoryId: 'air-conditioning',
    operation: 'AC refrigerant, compressor, evaporator',
  },
  {
    id: 'BODY',
    name: 'Bodywork',
    subcategoryId: 'bodywork',
    operation: 'Body repair, paint work',
  },
  {
    id: 'BRAKE_SYSTEM',
    name: 'Brake System',
    subcategoryId: 'brake-system',
    operation: 'Brake pads, brake discs, brake fluid',
  },
  {
    id: 'CLUTCH',
    name: 'Clutch',
    subcategoryId: 'clutch',
    operation: 'Clutch disc, pressure plate, slave cylinder',
  },
  {
    id: 'DIFFERENTIAL',
    name: 'Differential',
    subcategoryId: 'differential',
    operation: 'Differential oil, gears, seals',
  },
  {
    id: 'DOORS_WINDOWS',
    name: 'Doors & Windows',
    subcategoryId: 'doors-windows',
    operation: 'Window regulator, door locks, hinges',
  },
  {
    id: 'ELECTRICAL',
    name: 'Electrical Wiring',
    subcategoryId: 'electrical-wiring',
    // Battery is owned by `ENGINE_ELECTRICAL` (Czech `elektrické součásti
    // motoru (baterie)`); keep it out of the wiring row to avoid conflicting
    // category semantics in UI copy.
    operation: 'Wiring, fuses, connectors',
  },
  {
    id: 'ENGINE_ACCESSORIES',
    name: 'Engine Accessories',
    subcategoryId: 'engine-accessories',
    operation: 'Belts, alternator pulley, accessory mounts',
  },
  {
    id: 'ENGINE_COOLING',
    name: 'Engine Cooling',
    subcategoryId: 'engine-cooling',
    operation: 'Coolant, radiator, water pump',
  },
  {
    id: 'ENGINE_ELECTRICAL',
    name: 'Engine Electrics',
    subcategoryId: 'engine-electrical',
    // Backend Czech text is `elektrické součásti motoru (baterie)` —
    // battery is explicitly bundled here, not in `ELECTRICAL`.
    operation: 'Battery, starter motor, alternator, glow plugs',
  },
  {
    id: 'ENGINE_INTAKE',
    name: 'Engine Intake',
    subcategoryId: 'engine-air-fuel',
    operation: 'Air filter, intake manifold, MAF sensor',
  },
  {
    id: 'ENGINE_LUBRICATION',
    name: 'Engine Lubrication',
    subcategoryId: 'engine-lubrication',
    operation: 'Engine oil, oil filter, oil pump',
  },
  {
    id: 'ENGINE_MECHANICAL',
    name: 'Engine Mechanical',
    subcategoryId: 'engine-mechanical',
    operation: 'Engine block, pistons, valves, head gasket',
  },
  {
    id: 'EXHAUST_SYSTEM',
    name: 'Exhaust System',
    subcategoryId: 'engine-air-fuel',
    operation: 'Catalytic converter, muffler, exhaust pipes',
  },
  {
    id: 'FUEL_SYSTEM',
    name: 'Fuel System',
    subcategoryId: 'engine-air-fuel',
    operation: 'Fuel filter, fuel pump, injectors',
  },
  {
    id: 'HEATING',
    name: 'Heating',
    subcategoryId: 'heating',
    operation: 'Heater core, blower motor, heater hoses',
  },
  {
    id: 'IGNITION',
    name: 'Ignition',
    subcategoryId: 'engine-electrical',
    operation: 'Spark plugs, ignition coils, ignition module',
  },
  {
    id: 'INSTRUMENTS',
    name: 'Instruments & Diagnostics',
    subcategoryId: 'instruments-infotainment',
    // Training data (Czech `přístroje`) is dominated by diagnostic-tool work
    // (`diagnostika gds`, `pamat zavad nacitat`, fault-code reads) rather
    // than instrument-cluster repairs, so the user-visible operation has to
    // mention diagnostics or the row will surprise users.
    operation: 'Diagnostic scans, fault-code reads, instrument cluster, gauges',
  },
  {
    id: 'LIGHTING_EXTERIOR',
    name: 'Exterior Lighting',
    subcategoryId: 'lighting',
    operation: 'Headlights, tail lights, turn signals',
  },
  {
    id: 'LIGHTING_INTERIOR',
    name: 'Interior Lighting',
    subcategoryId: 'lighting',
    operation: 'Cabin lights, dome lights, courtesy lamps',
  },
  {
    id: 'LINKAGE_SYSTEM',
    name: 'Linkage System',
    subcategoryId: 'clutch',
    // Backend Czech text `pákové ústrojí` covers any lever/linkage actuation
    // across the drivetrain, brakes and pedals. Training data includes
    // handbrake levers, accelerator cable, brake-pedal bushings, half-shaft
    // CV boots and shifter linkage — the operation must read accordingly.
    operation: 'Shifter linkage, handbrake/pedal linkage, half-shaft boots',
  },
  {
    id: 'SAFETY_AND_ASSISTANCE_SYSTEMS',
    name: 'Safety & Assistance',
    subcategoryId: 'safety-systems',
    operation: 'ABS, ESP, ADAS sensors, airbags',
  },
  {
    id: 'STEERING',
    name: 'Steering',
    subcategoryId: 'steering',
    operation: 'Power steering pump, steering rack, tie rods',
  },
  {
    id: 'TIMING_MECHANISM',
    name: 'Timing Mechanism',
    subcategoryId: 'engine-mechanical',
    operation: 'Timing belt/chain, tensioner, idler',
  },
  {
    id: 'TRANSMISSION_AUTOMATIC',
    name: 'Automatic Transmission',
    subcategoryId: 'gearbox',
    operation: 'ATF fluid, valve body, torque converter',
  },
  {
    id: 'TRANSMISSION_MANUAL',
    name: 'Manual Transmission',
    subcategoryId: 'gearbox',
    operation: 'Gearbox oil, synchros, bearings',
  },
  {
    id: 'WHEELS_TIRES',
    name: 'Wheels & Tires',
    subcategoryId: 'wheels-tires',
    operation: 'Tire replacement, wheel alignment',
  },
  {
    id: 'WHEEL_SUSPENSION',
    name: 'Suspension',
    subcategoryId: 'suspension',
    operation: 'Shock absorbers, suspension arms',
  },
  {
    id: 'WIPERS_WASHERS',
    name: 'Wipers & Washers',
    subcategoryId: 'doors-windows',
    operation: 'Wiper blades, washer pump, washer fluid',
  },
  {
    id: 'COMFORT_FEATURES',
    name: 'Comfort Features',
    subcategoryId: 'comfort-features',
    operation: 'Seats, mirrors, central locking, sunroof, comfort electronics',
  },
  {
    id: 'INFOTAINMENT',
    name: 'Infotainment',
    subcategoryId: 'instruments-infotainment',
    operation: 'Head unit, navigation, speakers, multimedia interface',
  },
];

// ============================================================================
// ROW TYPE CONFIG
// ============================================================================

export const ROW_TYPE_THRESHOLDS = {
  DAMAGE: 0.8, // >= 80% = damage (critical)
  SERVICE: 0.5, // >= 50% = service (important)
  SUGGESTION: 0.3, // >= 30% = suggestion (advisory); below this is treated as noise
} as const;

export const ROW_TYPE_ICONS: Record<RowType, MiraIconKey> = {
  damage: 'mira/wrench',
  service: 'mira/screwdriver',
  suggestions: 'mira/lightbulb',
} as const;

// ============================================================================
// LOOKUP MAPS (derived from source arrays for O(1) access)
// ============================================================================

const buildCategoryMap = () => {
  const map = new Map<CategoryId, CategoryDefinition>();
  CATEGORIES.forEach((cat) => map.set(cat.id, cat));
  return map;
};

const buildSubcategoryMap = () => {
  const map = new Map<SubcategoryId, SubcategoryDefinition>();
  SUBCATEGORIES.forEach((sub) => map.set(sub.id, sub));
  return map;
};

const buildMainGroupMap = () => {
  const map = new Map<MainCategoryGroupId, MainGroupDefinition>();
  MAIN_GROUPS.forEach((group) => map.set(group.id, group));
  return map;
};

export const CATEGORY_MAP = buildCategoryMap();
export const SUBCATEGORY_MAP = buildSubcategoryMap();
export const MAIN_GROUP_MAP = buildMainGroupMap();
